import type { FC } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { trackRedeemCheckout } from "@loyalty/utils";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { toast } from "@jti/core-components/components/Toaster";
import { getErrorMessageFromError, isServerSide } from "@jti/fe-common";
import { CasAddressesApi, useGetUserAddresses, usePurchaseLoyaltyPrize } from "@jti/rest-api";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { fetchUserPoints, Status } from "@jti/store/slices/loyaltyCoreSlice";
import type { RootStoreState } from "@jti/store/types";
import { Dialog } from "@jti/ui/components/Dialog";
import { Form, useForm } from "@jti/ui/components/Form";
import { useDeviceBreakpoint } from "@jti/ui/hooks/useDeviceBreakpoint";
import { useUser } from "@jti/ui/hooks/useUser";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { PRIZE_TYPE_SUFFIX } from "../../hooks/useLoyaltyPrizeCta/constants";

import { EditAddress, HeaderSection, PrizeConfirmation, PrizeDetails } from "./components";
import { useRedeemDialogHeaderTranslations } from "./hooks";
import type { DialogStep, FormAddress, RedeemDialogV2Props } from "./RedeemDialogV2.types";

import styles from "./RedeemDialogV2.module.scss";

export const RedeemDialogV2: FC<RedeemDialogV2Props> = ({
  logoUrl,
  logoAlt,
  variant = "default",
  prizeData,
  isInEditor,
  isDialogOpen,
  prizesPageUrl,
  pointsBalance,
  onCloseDialog,
  handlePrizePurchase,
  quantityAriaLabel,
  increaseAriaLabel,
  decreaseAriaLabel,
  isLotteryAddressRequirementDisabled,
}) => {
  const config = useConfig();

  const defaultAddressValue = useMemo(() => {
    return {
      deliveryAddress: {
        city: "",
        countryCode: config.ISO_COUNTRY_CODE,
        countryName: config.COUNTRY_NAME,
        sublocality_level_2: "",
        id: "",
        postcode: "",
        provinceName: "",
        streetAdditional: "",
        streetName: "",
        streetNumber: "",
        landmark: "",
        neighborhood: "",
        premise: "",
        regionCode: "",
      },
    };
  }, [config.COUNTRY_NAME, config.ISO_COUNTRY_CODE]);

  const [selectedAddress, setSelectedAddress] = useState<FormAddress["deliveryAddress"]>();
  const [step, setStep] = useState<DialogStep>("details");
  const [prizeQuantity, setPrizeQuantity] = useState(1);
  const {
    points: { status },
  } = useSelector((state: RootStoreState) => state.loyaltyCore);

  const form = useForm<FormAddress>({ defaultValues: defaultAddressValue });
  const { user } = useUser();
  const dispatch = useDispatch();
  const { __, dictionary } = useTranslations();

  const {
    data: purchasedLoyaltyPrizeData,
    purchaseLoyaltyPrize,
    isLoading: isPurchaseLoyaltyPrizeLoading,
    reset: resetPurchaseLoyaltyPrize,
  } = usePurchaseLoyaltyPrize();

  const { prize_type: rewardType = "simplified", isAddressRequired, time_to } = prizeData;

  const isPrizePhysical = rewardType === "physical";
  const isAddressActuallyRequired =
    Boolean(isAddressRequired) && (isPrizePhysical || !isLotteryAddressRequirementDisabled);
  // The reward call must include the saved address whenever the prize requires one, even
  // when the editable address form is hidden (lottery with the requirement disabled). Fetch
  // the saved address so the reward payload can be populated without creating a new one.
  const shouldPrefillSavedAddress = Boolean(isAddressRequired);

  const { data: userAddressesData, refetch } = useGetUserAddresses(shouldPrefillSavedAddress);
  const casAddressApi = CasAddressesApi.init();

  const { dialogTitle, dialogDescription } = useRedeemDialogHeaderTranslations({
    step,
    rewardType,
    won: purchasedLoyaltyPrizeData?.won ?? undefined,
    timeTo: time_to,
  });
  const { breakpoint } = useDeviceBreakpoint();
  const { images, prize_uuid } = prizeData;
  const [mainImageUrl] = images ?? [];
  const shouldRenderForegroundImage = Boolean(mainImageUrl && variant === "blurredBackground");

  const isSmallScreen = breakpoint === "mobile";

  const isDetailsStepVisible = step === "details";
  const isEditAddressStepVisible = step === "editAddress";
  const isConfirmationStepVisible = step === "confirmation";

  const isInstantLotteryLost =
    rewardType.includes(PRIZE_TYPE_SUFFIX.instant) && step === "confirmation" && !purchasedLoyaltyPrizeData?.won;

  const handleChangeStepToConfirmation = useCallback(() => {
    setStep("confirmation");
  }, []);

  const handleEditAddress = useCallback(() => {
    setStep("editAddress");
  }, []);

  const handleChangeStepToDetails = useCallback(() => {
    setStep("details");
  }, []);

  const handleSaveNewAddress = async () => {
    const { deliveryAddress } = form.getValues();
    try {
      await casAddressApi.createAddress({
        ...deliveryAddress,
        firstname: user?.first_name ?? "",
        lastname: user?.last_name ?? "",
        telephone: user?.phone ?? "",
      });
    } catch (error) {
      console.error("Error creating address:", error);
    }

    setSelectedAddress({
      ...deliveryAddress,
      firstname: user?.first_name ?? "",
      lastname: user?.last_name ?? "",
    });
    refetch();
    handleChangeStepToDetails();
  };

  const handleSelectExistingAddress = (address: FormAddress["deliveryAddress"]) => {
    setSelectedAddress(address);
    handleChangeStepToDetails();
  };

  const handleGetUserAddressFromForm = () => {
    const { deliveryAddress } = form.getValues();
    return {
      ...deliveryAddress,
      firstname: user?.first_name ?? "",
      lastname: user?.last_name ?? "",
    };
  };

  const handlePurchasePrize = async () => {
    const { deliveryAddress } = form.getValues();

    try {
      await purchaseLoyaltyPrize({
        ciam_token: user?.ciam_token ?? "",
        consumer_uuid: user?.uuid ?? "",
        prize_uuid,
        quantity: prizeQuantity,
        ...(isAddressRequired ? { address: selectedAddress ?? handleGetUserAddressFromForm() } : {}),
      });

      if (handlePrizePurchase) {
        handlePrizePurchase();
      }

      if (isAddressActuallyRequired && userAddressesData?.addresses?.length === 0) {
        casAddressApi.createAddress({
          ...deliveryAddress,
          firstname: user?.first_name ?? "",
          lastname: user?.last_name ?? "",
          telephone: user?.phone ?? "",
        });
      }

      await dispatch(fetchUserPoints({ useLocalCache: false, dispatchUpdateEvent: true }));
      trackRedeemCheckout(prizeData);
      handleChangeStepToConfirmation();
    } catch (error) {
      const errorMessage = getErrorMessageFromError(error ?? {}, __("evo.fallback.error"), dictionary);
      toast.error(errorMessage);
    }
  };

  const resetDialogData = useCallback(() => {
    setStep("details");
    setPrizeQuantity(1);
    resetPurchaseLoyaltyPrize();
  }, [resetPurchaseLoyaltyPrize]);

  const handleCloseDialog = useCallback(() => {
    onCloseDialog();
    resetDialogData();
  }, [onCloseDialog, resetDialogData]);

  const handleInstantLotteryRetry = useCallback(() => {
    setStep("details");
  }, []);

  const handleRedirectToPrizesPage = () => {
    handleCloseDialog();
    if (!isServerSide()) {
      if (!prizesPageUrl || window.location.href.includes(prizesPageUrl)) {
        return;
      }
      window.location.href = prizesPageUrl;
    }
  };

  useEffect(() => {
    if (shouldPrefillSavedAddress && !selectedAddress && !!userAddressesData?.addresses?.length) {
      setSelectedAddress(userAddressesData?.addresses[0]);
    }
  }, [shouldPrefillSavedAddress, selectedAddress, userAddressesData]);

  if ((!user || !prizeData?.prize_uuid) && !isInEditor) {
    return null;
  }

  const isUserEligibleForReward = pointsBalance >= prizeData.points && !purchasedLoyaltyPrizeData?.won;

  return (
    <Dialog
      closable={true}
      noPadding={true}
      isOpened={isDialogOpen}
      className={styles.dialog}
      closeWrapperClassName={styles.closeButtonWrapper}
      fullscreen={isSmallScreen}
      onClose={handleCloseDialog}
      withContentPadding={false}
    >
      <Form form={form}>
        <div className={styles.container}>
          <section className={styles.contentWrapper}>
            <HeaderSection
              logoUrl={logoUrl}
              logoAlt={logoAlt}
              dialogTitle={dialogTitle}
              dialogDescription={dialogDescription}
            />
            {isDetailsStepVisible && (
              <PrizeDetails
                emailAddress={user?.email}
                prizeData={prizeData}
                rewardType={rewardType}
                pointsBalance={pointsBalance}
                prizeQuantity={prizeQuantity}
                isAddressBlockVisible={isAddressRequired}
                isLotteryAddressRequirementDisabled={isLotteryAddressRequirementDisabled}
                isConfirmButtonLoading={isPurchaseLoyaltyPrizeLoading || status === Status.PENDING}
                onCloseDialog={handleCloseDialog}
                userAddress={selectedAddress}
                onConfirm={handlePurchasePrize}
                onEditAddress={handleEditAddress}
                onSetPrizeQuantity={setPrizeQuantity}
                quantityAriaLabel={quantityAriaLabel}
                increaseAriaLabel={increaseAriaLabel}
                decreaseAriaLabel={decreaseAriaLabel}
              />
            )}

            {isConfirmationStepVisible && (
              <PrizeConfirmation
                rewardType={rewardType}
                rewardTitle={prizeData.name}
                prizesData={purchasedLoyaltyPrizeData}
                onComplete={isInstantLotteryLost ? handleInstantLotteryRetry : handleCloseDialog}
                onExploreMoreRewards={handleRedirectToPrizesPage}
                isUserEligibleForReward={isUserEligibleForReward}
                isInstantLotteryWon={purchasedLoyaltyPrizeData?.won ?? undefined}
              />
            )}
            {isEditAddressStepVisible && (
              <EditAddress
                addresses={userAddressesData?.addresses}
                onSaveNewAddress={handleSaveNewAddress}
                onCancelAddressSelections={handleChangeStepToDetails}
                onSelectExistingAddress={handleSelectExistingAddress}
              />
            )}
          </section>

          <section
            className={classNames(styles.imageWrapper, {
              [styles.imageWrapperSimplifiedConfirmationMobilePadding]:
                rewardType === "simplified" && isSmallScreen && isConfirmationStepVisible,
              [styles.imageWrapperBlurredBackground]: variant === "blurredBackground",
            })}
            style={mainImageUrl ? { "--redeem-dialog-background-image": `url('${mainImageUrl}')` } : undefined}
          >
            {shouldRenderForegroundImage && (
              <ImageBehaviour
                src={mainImageUrl}
                alt="prizeImage"
                className={classNames(styles.image, styles.imageBlurredBackground)}
                width={400}
                height={300}
              />
            )}
          </section>
        </div>
      </Form>
    </Dialog>
  );
};
